import { Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { Box } from '@mui/system';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useParams } from 'react-router-dom';
import Image from '../../external/fox-typescript/core/Image';
import { useForm } from '../../hooks/useForm';
import { SxStyles } from '../../model/utils/SxStyles';
import AppTheme, { COLORS, IAppTheme } from '../../utils/AppTheme';
import { RequestStatus } from '../../utils/RequestStatus';
import { AppTextField } from '../commons/AppTextField';
import { UploadImage } from '../commons/UploadImage';
import { AppTypography } from '../Typography/AppTypography';
import { PDLTypography } from '../Typography/PDLTypography';
import enviroment from '../../environment.json';
import SimpleButton from '../commons/SimpleButton';
import PdlAuthService from '../../services/PdlAuthService';
import AlertService from '../../services/AlertService';
import Logger from '../../external/pdl-common/utils/Logger';
import { VerifyEmailAlertBottomSheet } from '../commons/VerifyEmailAlertBottomSheet';
import TermsAndConditionsCheckbox from '../commons/TermsAndConditionsCheckbox';
import { WelcomeToPdlClub } from '../commons/WelcomeToPdlClub';
import { FooterPdlClub } from '../commons/FooterPdlClub';
import TopNavBar from '../core/LandingPageNavBar';
import { PasswordHelper } from '../commons/PasswordHelper';

export interface WaitlistCreateProfileForm {
  birthDate: string;
  password: string;
  confirmPassword: string;
  profileImageExternalId: string;
  recaptchaToken: string;
  accountWaitListExternalId: string;
  renterTermsAccepted: boolean;
}

export enum SheetType {
  NONE = 'none',
  MAILSENT = 'mail sent',
  PASSWORDHELPER = 'helper',
}

const logger = new Logger('BlackWaitListCreateProfile');

export function BlackWaitListCreateProfile() {
  const classes = useStyles();
  const { externalId } = useParams();

  const recaptchaRef = React.createRef<ReCAPTCHA>();
  const confirmPasswordInput = useRef<{ validate: () => void }>(null);

  const [form, validations, handleCustomChange, formIsValid, , setValidations] =
    useForm<WaitlistCreateProfileForm>({
      birthDate: '',
      password: '',
      confirmPassword: '',
      profileImageExternalId: '',
      recaptchaToken: '',
      accountWaitListExternalId: externalId!,
      renterTermsAccepted: false,
    });
  const [requestStatus, setRequestStatus] = useState<RequestStatus | undefined>(undefined);
  const [bottomSheetOpen, setBottomSheetOpen] = useState<SheetType>(SheetType.NONE);
  const [image, setImage] = useState<Image | undefined>(undefined);
  const [imageFile, setImageFile] = useState<File | undefined>(undefined);

  useEffect(() => {
    if (form.confirmPassword) {
      const isValid = confirmPasswordInput?.current?.validate();

      let updatedValidations = { ...validations };
      (updatedValidations as any).confirmPassword = isValid;
      setValidations(updatedValidations);
    }
  }, [form.password]);

  const handleSetImage = (profileImage: Image) => {
    setImage(profileImage);
    handleCustomChange('profileImageExternalId', profileImage.externalId!, true);
  };

  const handleRemoveImage = () => {
    if (requestStatus !== RequestStatus.SUCCESS) {
      setImage(undefined);
      setImageFile(undefined);
      handleCustomChange('profileImageExternalId', '', false);
    }
  };

  const handleOnCaptchaTokenChange = (token: string | null) => {
    handleCustomChange('recaptchaToken', token || '', !!token);
  };

  const fieldIsDisabled = () => {
    return requestStatus === RequestStatus.SUCCESS;
  };

  const onSubmit = async () => {
    setRequestStatus(RequestStatus.LOADING);

    const res = await PdlAuthService.signUpUsingWaitlist(form);
    res
      .onSuccess(() => {
        setRequestStatus(RequestStatus.SUCCESS);
        setBottomSheetOpen(SheetType.MAILSENT);
      })
      .onError((res) => {
        setRequestStatus(RequestStatus.ERROR);
        logger.error('Error trying to create profile', res);

        AlertService.showSnackCustomError(res.getContent());
        (window as any).grecaptcha.reset();
      });
  };

  const onCheckboxChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value: boolean = e.target.value === 'true';
    handleCustomChange(e.target.name, !value, !value);
  };

  return (
    <>
      <Box sx={{ backgroundColor: COLORS.black_000000 }}>
        <TopNavBar />
        <WelcomeToPdlClub />
        <Grid container direction="column" alignItems={'center'} sx={{ padding: '0em 2em' }}>
          <Grid container direction="column" sm={10} md={6}>
            <Grid item xs={12} sm={6} sx={muiStyle.gridHeader}>
              <AppTypography
                type={PDLTypography.largeHeading}
                customStyles={{
                  marginTop: '1.66em',
                  marginBottom: '1em',
                  textAlign: 'center',
                  color: COLORS.white_FFFFFF,
                }}
              >
                Welcome!
                <br />
                Create Your Profile
              </AppTypography>
            </Grid>

            <Grid item xs={12} sx={{ marginTop: '0.95em' }}>
              <div className={classes.divFlex}>
                <AppTextField
                  label={'Date of birth'}
                  name={'birthDate'}
                  value={form.birthDate}
                  onInputChange={handleCustomChange}
                  inputProps={{ type: 'date', disabled: fieldIsDisabled(), sx: muiStyle.input }}
                  labelProps={{ customClasses: classes.label, type: PDLTypography.smallHeading }}
                  realTimeValidation
                  required
                />

                <AppTextField
                  label={'Password'}
                  name={'password'}
                  value={form.password}
                  onInputChange={handleCustomChange}
                  inputProps={{
                    type: 'password',
                    disabled: fieldIsDisabled(),
                    autoComplete: 'new-password',
                    sx: muiStyle.input,
                  }}
                  labelProps={{ customClasses: classes.label, type: PDLTypography.smallHeading }}
                  realTimeValidation
                  required
                  restrict="strongPassword"
                  bottomRightElement={
                    <AppTypography
                      type={PDLTypography.smallParagraph}
                      children={'Valid format'}
                      customStyles={{
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        textAlign: 'right',
                        color: COLORS.white_FFFFFF,
                      }}
                      onClick={() => setBottomSheetOpen(SheetType.PASSWORDHELPER)}
                    />
                  }
                />

                <AppTextField
                  ref={confirmPasswordInput}
                  label={'Confirm Password'}
                  name={'confirmPassword'}
                  value={form.confirmPassword}
                  onInputChange={handleCustomChange}
                  inputProps={{ type: 'password', disabled: fieldIsDisabled(), sx: muiStyle.input }}
                  labelProps={{ customClasses: classes.label, type: PDLTypography.smallHeading }}
                  realTimeValidation
                  disableAutoComplete
                  required
                  customValidation={(text) => {
                    if (text !== form.password) {
                      return {
                        showError: true,
                        isValid: false,
                        message: 'Password do not match',
                      };
                    }
                    return { showError: false, isValid: true, message: '' };
                  }}
                />
              </div>
            </Grid>

            <Grid item xs={12} sm={6} sx={muiStyle.gridBottom}>
              <div className={classes.profileImage}>
                <UploadImage
                  image={image}
                  imageUrls={image ? [image.url] : undefined}
                  label="Profile image"
                  labelProps={{ customStyles: { marginBottom: '0.5em', marginTop: '1.42em' } }}
                  setImageFiles={(files) => {
                    if (files) {
                      setImageFile(files[0]);
                    }
                  }}
                  handleSetImage={handleSetImage}
                  imageFiles={imageFile ? [imageFile] : undefined}
                  onRemove={handleRemoveImage}
                />
              </div>

              <Box sx={{ textAlign: 'center' }}>
                <TermsAndConditionsCheckbox
                  name={'renterTermsAccepted'}
                  checked={form.renterTermsAccepted}
                  onChange={onCheckboxChange}
                />
              </Box>

              <Box sx={muiStyle.recaptchaBox}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  sitekey={enviroment.recaptchaSiteKey}
                  onChange={handleOnCaptchaTokenChange}
                />
              </Box>

              <Box sx={muiStyle.signUp}>
                <SimpleButton
                  sx={{
                    marginBottom: '0.5em',
                    marginTop: '2em',
                    width: '37vh',
                    textAlign: 'center',
                  }}
                  children={'Sign Up'}
                  onClick={onSubmit}
                  disabled={
                    !formIsValid() ||
                    requestStatus === RequestStatus.LOADING ||
                    requestStatus === RequestStatus.SUCCESS // To prevent user register twice
                  }
                  isLoading={requestStatus === RequestStatus.LOADING}
                />
              </Box>
            </Grid>
          </Grid>
        </Grid>
        <FooterPdlClub dense />
      </Box>

      <VerifyEmailAlertBottomSheet
        open={bottomSheetOpen === SheetType.MAILSENT}
        onClose={() => setBottomSheetOpen(SheetType.NONE)}
        email=""
        waitlist
      />
      {/* Password Helper */}
      <PasswordHelper
        open={bottomSheetOpen === SheetType.PASSWORDHELPER}
        onClose={() => setBottomSheetOpen(SheetType.NONE)}
      />
    </>
  );
}

const muiStyle = {
  gridHeader: {
    marginTop: '3.75em',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  gridBottom: {
    marginBottom: '2em',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  recaptchaBox: {
    display: 'flex',
    justifyContent: 'center',
    my: '1.2em',
  },
  input: {
    '.MuiInputBase-input': {
      backgroundColor: COLORS.white_FFFFFF,
      width: '34vh',
      height: '3vh',
    },
  },
  signUp: {
    marginTop: '1em',
    marginBottom: '2em',
    itemAlign: 'center',
  },
} as SxStyles;

const useStyles = makeStyles((theme: IAppTheme) => ({
  divFlex: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  bodyInvite: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  },
  renterTerms: {
    color: AppTheme.colors.persianBlue_2238CB,
    cursor: 'pointer',
    textDecoration: 'underline',
  },
  label: {
    color: COLORS.white_FFFFFF,
    marginTop: '1.5em',
    marginBottom: '0.5em',
    fontSize: '.813em',
  },
  profileImage: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: '37vh',
    maxWidth: '37vh',
  },
}));
